import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

export default function ChatInput({ onSend, isStreaming, onStop, disabled }) {
  const { t } = useTranslation('chat');
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);

  const handleSend = useCallback(() => {
    if (input.trim() && !isStreaming && !disabled) {
      onSend(input.trim());
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [input, isStreaming, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleInput = useCallback((e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  }, []);

  return (
    <div className="max-w-3xl mx-auto w-full">
      <div className="flex items-end gap-2 bg-muted rounded-2xl px-4 py-2">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={t('input_placeholder', 'Type a message...')}
          disabled={disabled}
          rows={1}
          className="flex-1 bg-transparent text-sm resize-none outline-none max-h-[200px] py-2 text-foreground placeholder:text-muted-foreground disabled:opacity-50"
        />

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {isStreaming ? (
                <Button
                  variant="destructive"
                  size="icon"
                  className="shrink-0 size-9 rounded-full"
                  onClick={onStop}
                >
                  <Square className="size-4" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  className="shrink-0 size-9 rounded-full"
                  onClick={handleSend}
                  disabled={!input.trim()}
                >
                  <Send className="size-4" />
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              {isStreaming ? t('stop', 'Stop') : t('send', 'Send')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
